import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMobileLayout } from '../../utils/useMobileLayout';

// Mirrors ContactList's options so a contact added here and one added on the
// member edit form can't end up with different role vocabularies.
export const CONTACT_ROLES = ['Owner', 'Manager', 'Emergency', 'Tenant', 'Caretaker', 'Other'];

const EMPTY = { name: '', phone: '', email: '', role: 'Owner' };

/**
 * Add or edit a single contact, modelled on PaymentDialog so the two
 * quick-actions on the member detail page behave identically.
 *
 * `onSave` re-throws on failure, which keeps the dialog open with the user's
 * input intact rather than dropping it behind a closed modal.
 */
const ContactDialog = ({ open, onClose, onSave, contact }) => {
  const { isMobile } = useMobileLayout();
  const [formData, setFormData] = useState(EMPTY);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    if (contact) {
      setFormData({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        role: contact.role || 'Owner',
      });
    } else {
      setFormData(EMPTY);
    }
  }, [contact, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // The backend requires a name (ContactWrite.name is min_length=1); catch
    // it here so the user gets a field-level message instead of a 422.
    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await onSave({
        name: formData.name.trim(),
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        role: formData.role || null,
      });
      onClose();
    } catch (err) {
      console.error('Error saving contact:', err);
      setError('Could not save this contact. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{contact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
            autoFocus
            error={Boolean(error) && !formData.name.trim()}
          />
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            fullWidth
            inputProps={{ inputMode: 'tel' }}
          />
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            fullWidth
          />
          <TextField
            label="Role"
            select
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            fullWidth
          >
            {CONTACT_ROLES.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={18} /> : null}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDialog;
